<template>
    <div class="charts">
        <div class="tab-content">
            <div v-if="showIncomeChart" :aria-label="incomeChartDescription">
                <income-chart :chartData="getNetIncomeChartData"
                              :detailChartData="getDetailChartData"
                              :chartOptions="netIncomeChartOptions"
                              :show-legend="false" />
            </div>
            <div v-if="showSourceTypeChart" :aria-label="incomeSourceTypeChartDescription">
                <income-chart :chartData="getSourceTypeChartData"
                              :chartOptions="sourceTypeChartOptions"
                              :show-legend="false"
                              :use-chart-data-legend-index="false" />
            </div>
            <div v-if="showSourceChart" :aria-label="incomeSourceChartDescription">
                <income-chart :chartData="getSourceChartData"
                              :chartOptions="sourceChartOptions"
                              :show-legend="false"
                              :use-chart-data-legend-index="false"
                              :highlight-on-legend-item-focus="true" />
            </div>

            <div class="mx-3 mt-1 mb-4 tab-header">
                <div v-show="tabsVisible" class="btn-group" role="group" aria-label="Income chart views">
                    <radio-button v-for="(item, i) in incomeChartViews"
                                  :ref="item"
                                  v-bind:key="i"
                                  name="IncomeChartOption"
                                  :label="item"
                                  :value="incomeChartViews[0]"
                                  @change="onTabClick(i)" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useStore } from "vuex";
import IncomeChart from "@/guiide/components/charts/IncomeChart.vue";
import RadioButton from "@/shared/components/RadioButton.vue";
import { HighchartsChartOptions } from "@/guiide/ts/highchartsChartOptions";
import { SeriesColour } from "@/guiide/ts/charts/seriesColour";
import { retireeDescriber } from "@/guiide/ts/retireeDescriber";
import { IncomeChartSeries } from "@/guiide/ts/response/charts/incomeChartSeries";
import { createSeriesId, mapIncomeSourceToIncomeSeries } from "@/guiide/ts/response/charts/chartUtils";
import { HighchartsIncomeChartSeries } from "@/guiide/ts/response/charts/highchartsIncomeChartSeries";
import { IncomeSourceChartSeries } from "@/guiide/ts/response/charts/incomeSourceChartSeries";
import IncomeChartClass from "@/guiide/ts/response/charts/incomeChartClass";
import { colorToGray } from "@/guiide/ts/utilities";

enum ChartType {
    NetIncome,
    SourceType,
    Source
}

const store = useStore();
const selectedTabIndex = ref(0);
let animate = ref(true);
const sourceTypeChartAvailable = store.state.configuration.designIncomeChartConfig === 'IncludeSourceType';
const sourceChartAvailable = store.state.configuration.designIncomeChartConfig === 'IncludeSource';
const incomeChartViews = [
    'Net income',
    ...(sourceTypeChartAvailable ? ['Source type'] : []),
    ...(sourceChartAvailable ? ['Source'] : [])
];
const tabsVisible = ref(incomeChartViews.length > 1);

const showIncomeChart = computed<boolean>(() => selectedTabIndex.value == 0);
const showSourceTypeChart = computed<boolean>(() => sourceTypeChartAvailable && selectedTabIndex.value == 1);
const showSourceChart = computed<boolean>(() => sourceChartAvailable && selectedTabIndex.value == 1);

const netIncomeChartOptions: HighchartsChartOptions = {
    description: "An income plan chart displaying the distribution of funds across retirement with tax and any expected shortfalls.",
    title: "Net income in each year",
    sharedTooltip: false,
    seriesHoverCursor: "pointer",
};
const sourceTypeChartOptions: HighchartsChartOptions = {
    description: "An income source type chart displaying a breakdown of funds by source type across retirement.",
    title: "Income by source type in each year",
    sharedTooltip: true,
    seriesHoverCursor: "default",
};
const sourceChartOptions: HighchartsChartOptions = {
    description: "An income source chart displaying a breakdown of funds by source across retirement.",
    title: "Income by source in each year",
    sharedTooltip: true,
    seriesHoverCursor: "default",
};

const chartDataHasLoaded = computed<boolean>(() => 
    store.state.annualIncomeSummaryData.length > 0 
    && store.state.annualIncomeByTypeData.length > 0 
    && store.state.annualIncomeBySourceData.length > 0);

const incomeChartDescription = computed<string>(() => "Your income chart. A description of the chart: " + retireeDescriber.getIncomeChartDescription());
const incomeSourceChartDescription = computed<string>(() => "Your income source chart. A description of the chart: " + retireeDescriber.getIncomeSourceChartDescription(store.state.annualIncomeBySourceData));
const incomeSourceTypeChartDescription = computed<string>(() => "Your income source type chart. A description of the chart: " + retireeDescriber.getIncomeSourceChartDescription(store.state.annualIncomeByTypeData));

const getNetIncomeChartData = computed<Array<HighchartsIncomeChartSeries>>(() => {
    if (!chartDataHasLoaded.value) {
        return [];
    }

    return configureEachSeriesHighchartsProperties(store.state.annualIncomeSummaryData, animate.value, true, ChartType.NetIncome);
});

const getDetailChartData = computed<Array<IncomeSourceChartSeries>>(() => {
    return store.state.annualIncomeByTypeData;
});

const getSourceTypeChartData = computed<Array<HighchartsIncomeChartSeries>>(() => {
    if (!chartDataHasLoaded.value) {
        return [];
    }

    const groupedChartData = mapIncomeSourceToIncomeSeries(store.state.retiree, store.state.annualIncomeByTypeData);
    return configureEachSeriesHighchartsProperties(groupedChartData, false, true, ChartType.SourceType);
});

const getSourceChartData = computed<Array<HighchartsIncomeChartSeries>>(() => {
    if (!chartDataHasLoaded.value) {
        return [];
    }

    const groupedChartData = mapIncomeSourceToIncomeSeries(store.state.retiree, store.state.annualIncomeBySourceData);
    return configureEachSeriesHighchartsProperties(groupedChartData, false, true, ChartType.Source);
});

function onTabClick(index: number) {
    animate.value = false;
    selectedTabIndex.value = index;
}

function configureEachSeriesHighchartsProperties(chartData: Array<IncomeChartSeries>, animateChart: boolean, enableMouseTracking: boolean,
    chartType: ChartType): Array<HighchartsIncomeChartSeries> {
    return chartData.map((incomeChartSeries) => {
        const id = createSeriesId(incomeChartSeries.name);
        const color = getSeriesColor(incomeChartSeries, chartType);
        const animation = animateChart ? { duration: 1200 } : false;

        return {
            id,
            ...incomeChartSeries,
            color,
            animation,
            enableMouseTracking
        };
    });
}

function getSeriesColor(incomeChartSeries: IncomeChartSeries, chartType: ChartType): string {

    const finalSalarySchemeColor = getFinalSalarySchemeOverrideColor(incomeChartSeries.class, incomeChartSeries.name);

    if (finalSalarySchemeColor) {
        return finalSalarySchemeColor;
    }

    const color = SeriesColour.getColour(incomeChartSeries.class);

    if (!store.state.configuration.chartsIncomeUseGreyscaleColors) {
        return color;
    }

    if (chartType !== ChartType.Source) {
        return color;
    }
    
    return colorToGray(color);
}

function getFinalSalarySchemeOverrideColor(incomeChartClass: IncomeChartClass, seriesName: string): string {
    
    if (incomeChartClass !== IncomeChartClass.FinalSalary) {
        return "";
    }

    const retiree = store.state.retiree;

    for (const finalSalaryScheme of retiree.finalSalaries.schemes) {
        if (seriesName.startsWith(finalSalaryScheme.name)) {
            return finalSalaryScheme.chartColor;
        }
    }

    return "";
}
</script>

<style lang="css" scoped>
    .charts .tab-header {
        display: grid;
    }
</style>
